import React, { useState, useEffect} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import InputMask from "react-input-mask"; 
import { ConfirmDialog } from "primereact/confirmdialog";  
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import { handleConfirmSave, handleNavigateBack, renderYesNoField, renderRangeField, convertYesNoFieldsToString, convertDateForInput, formatDateForBackend, formatDecimalForBackend, validateFields, renderLabelField, renderNumericField } from 'components/formUtils';  

const PreOperatorioForm = () => {
  const { GetTranslation } = useLanguage();
  const notify = useNotification(); 
  const navigate = useNavigate();
  const { id } = useParams(); 

  const [formData, setFormData] = useState({
    data_nascimento: "",
    sexo: "",
    altura: "",
    peso: "",
    imc: "",  // IMC será calculado automaticamente
    asc: "",  // ASC será calculado automaticamente
    has: "",
    dm: "",
    dmid: "",
    dpoc: "",
    iam_previo: "",
    ave_previo: "",
    fa_pre_operatoria: "",
    daop: "",
    pre_habilitado: "",
    cirurgia_toracica_previa: "",
    feve: "",
    creatinina: "",
    angioplastia_previa: "",
  });

  const token = localStorage.getItem("token");

  // Função para calcular IMC (peso / altura^2)
  const calcularIMC = (peso, altura) => {
    if (peso && altura) {
      const pesoNumerico = parseFloat(peso.replace(',', '.'));
      const alturaNumerica = parseFloat(altura.replace(',', '.'));
      return (pesoNumerico / (alturaNumerica ** 2)).toFixed(2);
    }
    return "";
  };

  // Função para calcular ASC (Área de Superfície Corporal)
  const calcularASC = (peso, altura) => {
    if (peso && altura) {
      const pesoNumerico = parseFloat(peso.replace(',', '.'));
      const alturaNumerica = parseFloat(altura.replace(',', '.')) * 100;  // Altura convertida em cm
      return (0.007184 * (pesoNumerico ** 0.425) * (alturaNumerica ** 0.725)).toFixed(2);
    }
    return "";
  };

  // Atualiza automaticamente o IMC e ASC ao alterar peso ou altura
  useEffect(() => {
    const { peso, altura } = formData;
    const newIMC = calcularIMC(peso, altura);
    const newASC = calcularASC(peso, altura);
    setFormData((prev) => ({ ...prev, imc: newIMC, asc: newASC }));
  }, [formData.peso, formData.altura]);

  const yesNoFields = [
    "has", "dm", "dmid", "dpoc", "iam_previo", "ave_previo", 
    "fa_pre_operatoria", "daop", "pre_habilitado", 
    "cirurgia_toracica_previa", "angioplastia_previa"
  ];

  // Carrega os dados ao editar
  useEffect(() => {
    if (id) {
      fetch(`${process.env.REACT_APP_API_URL}/pre_operatorio/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            // Converte os campos de Sim/Não para strings
            const convertedData = convertYesNoFieldsToString(data.data, yesNoFields);
            
            // Converte a data de nascimento no formato correto para exibição
            convertedData.data_nascimento = convertDateForInput(convertedData.data_nascimento);

            setFormData(convertedData);
          } else {
            notify({
              type: "error",
              message: data.error || "Erro ao buscar os dados"
            });
          }
        })
        .catch(() => {
          notify({
            type: "error",
            message: "Erro ao buscar os dados"
          });
        });
    }
  }, [id, token, notify]);
  

  const handleSubmit = () => {
    const requiredFields = ['data_nascimento', 'sexo', 'altura', 'peso'];

    if (!validateFields(formData, requiredFields, GetTranslation, notify)) return;

    const formattedData = {
      ...formData,
      data_nascimento: formatDateForBackend(formData.data_nascimento),
      peso: formatDecimalForBackend(formData.peso),
      altura: formatDecimalForBackend(formData.altura)
    };

    const apiUrl = id
      ? `${process.env.REACT_APP_API_URL}/pre_operatorio/update/${id}`
      : `${process.env.REACT_APP_API_URL}/pre_operatorio/add`;

    const method = id ? "PUT" : "POST";

    fetch(apiUrl, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(formattedData)
    })
      .then((response) => {
        if (response.ok) {
          notify({
            type: "success",
            message: id ? "Pré-Operatório atualizado com sucesso!" : "Pré-Operatório adicionado com sucesso!"
          });
        } else {
          response.json().then((data) => {
            notify({
              type: "error",
              message: `Erro: ${data.error}`
            });
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao enviar os dados"
        });
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateDate = (date) => {
    const [day, month, year] = date.split("/").map(Number);

    if (!day || !month || !year || day > 31 || month > 12) {
      return { isValid: false, message: GetTranslation("date_invalid") };
    }

    const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (day > daysInMonth[month - 1]) {
      return { isValid: false, message: GetTranslation("day_invalid") };
    }

    const today = new Date();
    const enteredDate = new Date(year, month - 1, day);

    if (enteredDate > today) {
      return { isValid: false, message: GetTranslation("date_future")  };
    }

    const minDate = new Date(today.getFullYear() - 120, today.getMonth(), today.getDate());
    if (enteredDate < minDate) {
      return { isValid: false, message: GetTranslation("date_past")  };
    }

    return { isValid: true, message: "" };
  };
  
  const handleBlurDate = (e) => {
    const { isValid, message } = validateDate(e.target.value);
    if (!isValid) {
      notify({
        type: "error",
        message: message,
      });
    }
  };

  const confirmSave = handleConfirmSave(formData, ['data_nascimento', 'sexo', 'altura', 'peso'], validateFields, GetTranslation, notify, handleSubmit);

  const handleBack = handleNavigateBack(navigate, id);


  return (
    <div className="card p-3">
      <ConfirmDialog />
      <h4>{id ? GetTranslation("Edit_PreOperatorio") : GetTranslation("Add_PreOperatorio")}</h4>
      <form className="row g-3">
        <div className="col-md-6">
          <label className="form-label">{GetTranslation("data_nascimento")}</label>
          <InputMask
            mask="99/99/9999"
            className="form-control"
            name="data_nascimento"
            value={formData.data_nascimento}
            onChange={handleChange}
            onBlur={handleBlurDate}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">{GetTranslation("Sexo")}</label>
          <select
            className="form-select"
            name="sexo"
            value={formData.sexo}
            onChange={handleChange}
            required
          >
            <option value="">{GetTranslation("Select_Sexo")}</option>
            <option value="1">{GetTranslation("Masculino")}</option>
            <option value="0">{GetTranslation("Feminino")}</option>
          </select>
        </div>


        {renderNumericField(formData, setFormData, "altura", "Altura (cm)", 2, GetTranslation)}
        {renderNumericField(formData, setFormData, "peso", "Peso (kg)", 2, GetTranslation)}


        {renderLabelField("IMC", formData.imc, GetTranslation, "(kg/m2)")}
        {renderLabelField("ASC", formData.asc, GetTranslation, "(m2)")}


        {/* Usando os métodos utilitários para os campos "Sim/Não" */}
        {renderYesNoField(formData, handleChange, "has", "HAS", GetTranslation)}
        {renderYesNoField(formData, handleChange, "dm", "DM", GetTranslation)}
        
        {formData.dm === "1" && renderYesNoField(formData, handleChange, "dmid", "DMid", GetTranslation)}
        {renderYesNoField(formData, handleChange, "dpoc", "DPOC", GetTranslation, "(em uso de broncodilatador inalatório)")}
        {renderYesNoField(formData, handleChange, "iam_previo", "IAM prévio", GetTranslation)}
        {renderYesNoField(formData, handleChange, "ave_previo", "AVE prévio", GetTranslation)}
        {renderYesNoField(formData, handleChange, "fa_pre_operatoria", "FA", GetTranslation, "(prévia à cirurgia)")}
        {renderYesNoField(formData, handleChange, "daop", "DAOP", GetTranslation)}
        {renderYesNoField(formData, handleChange, "pre_habilitado", "pre_habilitacao", GetTranslation, "")}
        {renderYesNoField(formData, handleChange, "cirurgia_toracica_previa", "Cirurgia torácica prévia", GetTranslation)}
        {renderYesNoField(formData, handleChange, "angioplastia_previa", "Angioplastia prévia", GetTranslation)}

        {/* Usando os métodos utilitários para os campos numéricos */}
        {renderRangeField(formData, setFormData, "feve", "FEVE", 0, 100, 2, GetTranslation, "(%)")}
        {renderRangeField(formData, setFormData, "creatinina", "Creatinina", 0, 30, 2, GetTranslation, "(mg/dl)")}

        
        <div className="col-12 d-flex justify-content-between">
          <Button
            label={GetTranslation("Voltar")}
            className="btn btn-secondary"
            onClick={handleBack}
          />
          <Button
            label={id ? GetTranslation("Update") : GetTranslation("Create")}
            className="btn btn-primary"
            type="submit"
            onClick={(e) => confirmSave(e)} // Chama a função dentro de uma callback
          />

        </div>
      </form>
    </div>
  );
};

export default PreOperatorioForm;